import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Flex, Input, message, Modal, Popconfirm, Space, Table, Tree } from "antd";
import { CheckCircleFilled, CloseCircleFilled, DeleteOutlined, DownOutlined, EditOutlined, EllipsisOutlined, FolderAddOutlined, FolderOpenOutlined, FolderOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { ColumnType, TablePaginationConfig } from "antd/es/table";
import type { DataNode, TreeProps } from "antd/es/tree";

import DeviceGroupApi from "../../api/DeviceGroupApi";
import DeviceApi from "../../api/DeviceApi";
import PermissionWrapper from "../../components/permission/PermissionWrapper";
import {
    DeviceGroupManagementWrapper,
    ToolBarWrapper, TreeWrapper,
    TreeContentWrapper,
    LeftPanelWrapper,
    RightPanelWrapper,
    PanelTitle
} from "./DeviceGroupManagement.styled";
import EditGroupModal from "./components/edit-group-modal/EditGroupModal";
import DeviceSelection from "../../components/ui-components/device-selection/DeviceSelection";

interface DeviceGroupManagementProps {

}

interface TableParams {
    pagination?: TablePaginationConfig;
}

interface DeviceGroup {
    id: string;
    name: string;
    parentId?: string;
    children?: DeviceGroup[];
}

interface DeviceView {
    device: {
        id: string;
        name: string;
    };
    tenant: {
        name: string;
    };
    deviceTemplate?: {
        name: string;
    };
    node: {
        status: string;
        lastSeenTime: number;
    };
}

const DeviceGroupManagement: React.FC<DeviceGroupManagementProps> = (props)=> {
    // api
    let navigate = useNavigate();
    let deviceGroupApi = new DeviceGroupApi();
    let deviceApi = new DeviceApi();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [treeNode, setTreeNode] = useState<DataNode[]>([]);
    let [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    let [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
    let [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    let [loading, setLoading] = useState(false);

    // right panel - device list
    let [devices, setDevices] = useState<DeviceView[]>([]);
    let [deviceLoading, setDeviceLoading] = useState(false);
    let [deviceTableParams, setDeviceTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
        },
    });
    let [searchDevice, setSearchDevice] = useState("");

    // modals
    let [openEditModal, setOpenEditModal] = useState(false);
    let [openAddDeviceModal, setOpenAddDeviceModal] = useState(false);
    let [editingGroup, setEditingGroup] = useState<DeviceGroup | null>(null);

    // events
    let onTreeSelect: TreeProps['onSelect'] = (selectedKeysValue, info) => {
        if (selectedKeysValue.length > 0) {
            setSelectedKeys(selectedKeysValue);
            setSelectedGroupId(selectedKeysValue[0] as string);
        } else {
            setSelectedKeys([]);
            setSelectedGroupId(null);
            setDevices([]);
        }
    };

    let onTreeExpand = (expandedKeysValue: React.Key[]) => {
        setExpandedKeys(expandedKeysValue);
    };

    let onCreateGroup = (groupDataNode: DataNode) => {
        setEditingGroup({parentId: (groupDataNode as any).group.id} as any);
        setOpenEditModal(true);
    };

    let onEditGroup = (group: DataNode) => {
        setEditingGroup((group as any).group);
        setOpenEditModal(true);
    };

    let onDeleteGroup = async (group: DataNode) => {
        await deviceGroupApi.delete((group as any).group.id);
        messageApi.success("删除分组成功");
        loadTreeData();
    };

    let onAddDevice = () => {
        if (!selectedGroupId) {
            messageApi.warning("请先选择一个分组");
            return;
        }
        if (selectedGroupId == '0') {
            messageApi.warning("不能添加设备到根分组下");
            return;
        }
        setOpenAddDeviceModal(true);
    };

    let onDeleteDevice = async (deviceId: string) => {
        await deviceGroupApi.removeDevices(selectedGroupId, [deviceId]);
        messageApi.success("移除设备成功");
        loadGroupDevices();
    };

    let onViewDevice = (deviceId: string) => {
        navigate(`/device-management/device/${deviceId}`);
    };

    let onEditModalClose = () => {
        setOpenEditModal(false);
        setEditingGroup(null);
    };

    let onEditModalComplete = () => {
        setOpenEditModal(false);
        setEditingGroup(null);
        loadTreeData();
    };

    let onAddDeviceModalClose = () => {
        setOpenAddDeviceModal(false);
    };

    let onAddDeviceModalComplete = async (selectedDeviceViews: any[]) => {
        let selectedDeviceIds = selectedDeviceViews.map((deviceView: any) => deviceView.device.id);
        await deviceGroupApi.addDevices(selectedGroupId, selectedDeviceIds);
        messageApi.success("添加设备成功");
        setOpenAddDeviceModal(false);
        loadGroupDevices();
    };

    let onDeviceSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchDevice(e.target.value);
    };

    let onDeviceTableChange = (pagination: any) => {
        deviceTableParams.pagination = pagination;
        setDeviceTableParams({ pagination: pagination });
        loadGroupDevices();
    };

    // helper functions
    let findGroupById = (nodes: DataNode[], id: string): DataNode | null => {
        for (let node of nodes) {
            if (node.key === id) {
                return node;
            }
            if (node.children) {
                let found = findGroupById(node.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    // load functions
    let loadTreeData = async () => {
        setLoading(true);
        try {
            let tree = (await deviceGroupApi.getTree()).data.data;
            let buildTreeNode = (group: any): DataNode => {
                let dataNode: any = {
                    title: group.name,
                    key: group.id,
                    icon: <FolderOutlined />,
                    group: group,
                };
                if (group.children != null && group.children.length > 0) {
                    dataNode.children = group.children.map((child: any) => buildTreeNode(child));
                }
                return dataNode;
            };

            let treeNode = buildTreeNode(tree);
            treeNode.title = '根分组';
            setTreeNode([treeNode]);

            // expand root by default
            let defaultExpandedKeys: React.Key[] = [treeNode.key];
            setExpandedKeys(defaultExpandedKeys);
        } finally {
            setLoading(false);
        }
    };

    let loadGroupDevices = async () => {
        if (!selectedGroupId) {
            return;
        }

        setDeviceLoading(true);
        try {
            // get devices in the selected group
            let devicesPage = (await deviceGroupApi.getGroupDeviceViews(selectedGroupId)).data.data;
            setDevices(devicesPage.elements);
        } finally {
            setDeviceLoading(false);
        }
    };

    // device columns
    let deviceColumns: ColumnType<DeviceView>[] = [
        {
            title: '状态',
            dataIndex: 'node.status',
            key: "node.status",
            align: 'center',
            render: (text, record, index)=> {
                return record.node.status == 'ONLINE' 
                    ? <CheckCircleFilled style={{color: 'green'}} />
                    : <CloseCircleFilled style={{color: 'grey'}} />
            }
        },
        {
            title: '设备名称',
            dataIndex: 'device.name',
            key: 'device.name',
            align: 'center',
        },
        {
            title: '设备模板',
            dataIndex: 'template.name',
            key: 'template.name',
            align: 'center',
            render: (text: any, record: DeviceView, index: number) => {
                return <span>{record.deviceTemplate?.name || '-'}</span>
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return <Space size="middle">
                    <a onClick={()=> onViewDevice(record.device.id)}>查看详情</a>
                    <Popconfirm
                            title="从分组中移除此设备"
                            description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将仅从此分组移除此设备，不会删除设备相关数据，确认要移除么?</div> }
                            onConfirm={()=> onDeleteDevice(record.device.id)}
                            okText="确定"
                            cancelText="取消"
                        > 
                        <a>移除设备</a>
                    </Popconfirm>
                </Space>
            }
        }
    ];

    // effects
    useEffect(() => {
        loadTreeData();
    }, []);

    useEffect(() => {
        if (selectedGroupId) {
            loadGroupDevices();
        }
    }, [selectedGroupId]);

    useEffect(() => {
        if (selectedGroupId) {
            loadGroupDevices();
        }
    }, [searchDevice]);

    // render
    let titleRender = (dataNode: DataNode) => {
        let menuItems = [
            {key: 'add-group', label: <span onClick={()=> onCreateGroup(dataNode)}>新增子分组</span>, icon: <FolderAddOutlined />},
            {key: 'delete-group', label: <span onClick={()=> onDeleteGroup(dataNode)}>删除分组</span>, icon: <DeleteOutlined />},
            {key: 'edit-group', label: <span onClick={()=> onEditGroup(dataNode)}>编辑分组</span>, icon: <EditOutlined />},
        ];

        return <span style={{fontSize: '14px'}}>
            <span>{typeof (dataNode.title) == 'function' ? dataNode.title(dataNode) : dataNode.title}</span>
            <Dropdown menu={{ items: menuItems }}>
                <EllipsisOutlined style={{fontSize: '14px', fontWeight: 'bold', marginLeft: '100px'}}/>
            </Dropdown>
        </span>
    };

    return (
        <DeviceGroupManagementWrapper>
            {contextHolder}
            <TreeContentWrapper>
                <LeftPanelWrapper>
                    <PanelTitle>分组列表</PanelTitle>
                    <TreeWrapper>
                        <Tree
                            showLine={true}
                            showIcon={false}
                            expandedKeys={expandedKeys}
                            selectedKeys={selectedKeys}
                            onExpand={onTreeExpand}
                            onSelect={onTreeSelect}
                            treeData={treeNode}
                            titleRender={titleRender}
                            switcherIcon={<DownOutlined />}
                            loading={loading}
                        />
                    </TreeWrapper>
                </LeftPanelWrapper>

                <RightPanelWrapper>
                    <PanelTitle>
                        {selectedGroupId ? '分组设备列表' : '请选择分组'}
                    </PanelTitle>
                    <ToolBarWrapper style={{ margin: '10px 0' }}>
                        <Flex justify="space-between" style={{width: '100%'}}>
                            <PermissionWrapper permissionCodePath="device-group.device.add-device">
                                <Button type="primary" icon={<PlusOutlined />} onClick={onAddDevice}>添加设备</Button>
                            </PermissionWrapper>`
                            <Input
                                placeholder="请输入设备名称搜索"
                                suffix={<SearchOutlined />}
                                value={searchDevice}
                                onChange={onDeviceSearchChange}
                                style={{ width: 300 }}
                            />
                        </Flex>
                    </ToolBarWrapper>
                    <Table
                        rowKey={(record) => record.device.id}
                        columns={deviceColumns}
                        dataSource={devices}
                        pagination={deviceTableParams.pagination}
                        loading={deviceLoading}
                        onChange={onDeviceTableChange}
                    />
                </RightPanelWrapper>
            </TreeContentWrapper>

            <Modal
                open={openEditModal}
                onCancel={onEditModalClose}
                footer={null}
                closable={false}
                destroyOnClose={true}
                width={600}
            >
                <EditGroupModal
                    editingGroup={editingGroup}
                    onClose={onEditModalClose}
                    onComplete={onEditModalComplete}
                />
            </Modal>

            <Modal
                open={openAddDeviceModal}
                onCancel={onAddDeviceModalClose}
                footer={null}
                closable={false}
                destroyOnClose={true}
                width={1200}
            >
                <DeviceSelection
                    multiSelect={true}
                    onCancel={onAddDeviceModalClose}
                    onOk={onAddDeviceModalComplete}
                />
            </Modal>
        </DeviceGroupManagementWrapper>
    );
}

export default DeviceGroupManagement;
